import React from "react";
import { useTranslation } from "react-i18next";

import { PaymentMethod } from "./types";
import {
    Card,
    SectionHeader,
    SectionTitle,
    ToggleContainer,
    ToggleLabel,
    Toggle,
    SmallToggle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    VisibilityButton,
    ResetButton,
    ResetAllButton,
    MethodSection,
    MethodSectionHeader,
    MethodIconCircle,
    MethodName,
    DisabledOverlay,
} from "./styled";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { EyeOffIcon } from "@components/Common/SvgIcons/EyeOffIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type PaymentMethodsCardProps = {
    mainColor: string;
    languages: Array<{ code: string; label: string }>;
    selectedLang: string;
    onLanguageChange: (lang: string) => void;
    paymentEnabled: boolean;
    onPaymentEnabledToggle: () => void;
    paymentMethods: PaymentMethod[];
    defaultPaymentMethods: PaymentMethod[];
    methodDescVisible: Record<string, boolean>;
    onToggleMethodDescVisible: (id: string) => void;
    onTogglePaymentMethod: (id: string) => void;
    onPaymentMethodTitleChange: (id: string, value: string) => void;
    onPaymentMethodDescChange: (id: string, value: string) => void;
    onResetMethodTitle: (id: string) => void;
    onResetMethodDesc: (id: string) => void;
    onResetAll: () => void;
};

export function PaymentMethodsCard({
    mainColor,
    languages,
    selectedLang,
    onLanguageChange,
    paymentEnabled,
    onPaymentEnabledToggle,
    paymentMethods,
    defaultPaymentMethods,
    methodDescVisible,
    onToggleMethodDescVisible,
    onTogglePaymentMethod,
    onPaymentMethodTitleChange,
    onPaymentMethodDescChange,
    onResetMethodTitle,
    onResetMethodDesc,
    onResetAll,
}: PaymentMethodsCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-payment-methods-card">
            <SectionHeader>
                <SectionTitle>{t("Payment methods")}</SectionTitle>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                    }}
                >
                    <ResetAllButton
                        type="button"
                        data-testid="scan-to-payment-methods-reset"
                        mainColor={mainColor}
                        onClick={onResetAll}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetAllButton>
                    <ToggleContainer>
                        <ToggleLabel>
                            {paymentEnabled
                                ? t("Active")
                                : t("Disable advertising")}
                        </ToggleLabel>
                        <Toggle
                            data-testid="scan-to-payment-toggle-enabled"
                            isActive={paymentEnabled}
                            mainColor={mainColor}
                            onClick={onPaymentEnabledToggle}
                            aria-label={t("Enable payment")}
                        />
                    </ToggleContainer>
                </div>
            </SectionHeader>

            {paymentEnabled ? (
                <>
                    <SectionTitle style={{ marginBottom: "15px" }}>
                        {t("Title and Description")}
                    </SectionTitle>

                    <LanguageTabs data-testid="scan-to-payment-methods-language-tabs">
                        {languages.map((lang) => (
                            <LanguageTab
                                key={lang.code}
                                data-testid={`scan-to-payment-methods-lang-${lang.code}`}
                                isActive={selectedLang === lang.code}
                                mainColor={mainColor}
                                onClick={() => onLanguageChange(lang.code)}
                            >
                                {lang.label}
                            </LanguageTab>
                        ))}
                    </LanguageTabs>

                    <div
                        style={{
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "10px",
                        }}
                    >
                        {paymentMethods.map((method) => {
                            const def = defaultPaymentMethods.find(
                                (d) => d.id === method.id
                            )!;
                            return (
                                <MethodSection
                                    key={method.id}
                                    data-testid={`scan-to-payment-method-section-${method.id}`}
                                >
                                    <MethodSectionHeader>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                            }}
                                        >
                                            <MethodIconCircle
                                                mainColor={mainColor}
                                            >
                                                <svg viewBox="0 0 24 24">
                                                    <path d={method.icon} />
                                                </svg>
                                            </MethodIconCircle>
                                            <MethodName>
                                                {def.title[selectedLang] ||
                                                    def.title.fr}
                                            </MethodName>
                                        </div>
                                        <SmallToggle
                                            data-testid={`scan-to-payment-toggle-method-${method.id}`}
                                            isActive={method.enabled}
                                            mainColor={mainColor}
                                            onClick={() =>
                                                onTogglePaymentMethod(method.id)
                                            }
                                            aria-label={t(
                                                "Toggle payment method"
                                            )}
                                        />
                                    </MethodSectionHeader>

                                    {/* Column headers */}
                                    <InputRow
                                        style={{
                                            borderBottom: "1px solid #e0e0e0",
                                            paddingBottom: "10px",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <InputLabel></InputLabel>
                                        <InputLabel style={{ fontWeight: 500 }}>
                                            {t("Default text")}
                                        </InputLabel>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                width: "100%",
                                            }}
                                        >
                                            <InputLabel
                                                style={{ fontWeight: 500 }}
                                            >
                                                {t("Customized")}
                                            </InputLabel>
                                            <ResetAllButton
                                                data-testid={`scan-to-payment-reset-method-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() => {
                                                    onResetMethodTitle(
                                                        method.id
                                                    );
                                                    onResetMethodDesc(
                                                        method.id
                                                    );
                                                }}
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetAllButton>
                                        </div>
                                    </InputRow>

                                    {/* Title row */}
                                    <InputRow>
                                        <InputLabel>{t("Title")}</InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-method-default-title-${method.id}`}
                                            value={
                                                def.title[selectedLang] ||
                                                def.title.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-method-custom-title-${method.id}`}
                                                value={
                                                    method.title[
                                                        selectedLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onPaymentMethodTitleChange(
                                                        method.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.title[selectedLang] ||
                                                    def.title.fr ||
                                                    ""
                                                }
                                            />
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-method-title-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetMethodTitle(
                                                        method.id
                                                    )
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>

                                    {/* Description row */}
                                    <InputRow>
                                        <InputLabel>
                                            {t("Description")}
                                        </InputLabel>
                                        <Input
                                            data-testid={`scan-to-payment-method-default-desc-${method.id}`}
                                            value={
                                                def.description[selectedLang] ||
                                                def.description.fr ||
                                                ""
                                            }
                                            readOnly
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                cursor: "not-allowed",
                                            }}
                                        />
                                        <InputWrapper>
                                            <Input
                                                data-testid={`scan-to-payment-method-custom-desc-${method.id}`}
                                                value={
                                                    method.description[
                                                        selectedLang
                                                    ] || ""
                                                }
                                                onChange={(e) =>
                                                    onPaymentMethodDescChange(
                                                        method.id,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={
                                                    def.description[
                                                        selectedLang
                                                    ] ||
                                                    def.description.fr ||
                                                    ""
                                                }
                                            />
                                            <VisibilityButton
                                                data-testid={`scan-to-payment-visibility-method-desc-${method.id}`}
                                                isVisible={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                }
                                                onClick={() =>
                                                    onToggleMethodDescVisible(
                                                        method.id
                                                    )
                                                }
                                                title={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-label={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                        ? t("Hide")
                                                        : t("Show")
                                                }
                                                aria-pressed={
                                                    methodDescVisible[
                                                        method.id
                                                    ] !== false
                                                }
                                            >
                                                {methodDescVisible[
                                                    method.id
                                                ] !== false ? (
                                                    <EyeIcon />
                                                ) : (
                                                    <EyeOffIcon />
                                                )}
                                            </VisibilityButton>
                                            <ResetButton
                                                data-testid={`scan-to-payment-reset-method-desc-${method.id}`}
                                                mainColor={mainColor}
                                                onClick={() =>
                                                    onResetMethodDesc(method.id)
                                                }
                                                title={t("Reset")}
                                                aria-label={t("Reset")}
                                            >
                                                <ResetIcon
                                                    style={{
                                                        width: ICON_SIZE_SMALL,
                                                        height: ICON_SIZE_SMALL,
                                                    }}
                                                />
                                            </ResetButton>
                                        </InputWrapper>
                                    </InputRow>
                                </MethodSection>
                            );
                        })}
                    </div>
                </>
            ) : (
                <DisabledOverlay>
                    {t("All actions are disabled. The section will be hidden.")}
                </DisabledOverlay>
            )}
        </Card>
    );
}
